import { lazy, Suspense, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import {
  markerTone,
  sortByState,
  TONE_COLOR_TOKEN,
  type LiveVehicle,
} from '@raad/live-markers';

import { InstallationPanel } from '@/features/installations/InstallationPanel';
import { GoLivePanel } from '@/features/provisioning/GoLivePanel';
import { useAdminLive, adminLiveToMarker, type AdminLiveVehicle } from '@/features/live/useAdminLive';
import { BottomSheet } from '@/features/live/BottomSheet';

/**
 * Global live — every device, every tenant, one screen (slice 17d; parity redesign
 * slice 84).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS SHOWS THAT NO CUSTOMER SCREEN CAN, AND WHY THAT IS THE POINT.
 * ─────────────────────────────────────────────────────────────────────────────
 *   - **Vehicles the customer cannot see.** `live: false` is the slice-18 go-live
 *     gate: provisioned, reporting, invisible on every tenant surface by design.
 *   - **Suspended tenants still reporting.** Slice 20 hides the view, never the data.
 *
 * SLICE 84 — the map is now the SHARED fleet map (`GlobalFleetMap`): real silhouettes
 * coloured by state and rotated by heading, smooth glide, tap-to-zoom, clustering.
 * The list gains SEARCH (plate / name / tenant) and is sorted by state exactly like
 * the tenant list (shared `sortByState`), with a state dot whose colour comes from the
 * same `markerTone` the map paints. At phone width the whole screen is a full-screen
 * map with the list in a bottom sheet; selecting a vehicle collapses the sheet to a
 * peek with its card and zooms the map close to it.
 *
 * Selecting a vehicle still opens the same `InstallationPanel` + `GoLivePanel` (the
 * go-live gate), unchanged and still super-admin-gated.
 *
 * THE MAP IS LAZY-LOADED — MapLibre is the largest thing in this bundle.
 */
const GlobalFleetMap = lazy(() =>
  import('@/features/live/GlobalFleetMap').then((m) => ({ default: m.GlobalFleetMap })),
);

/** The state-dot colour — read from the SAME token the map silhouette paints, so the
 *  list and the map can never disagree. A CSS `var()` string (theme-reactive, no JS
 *  resolution). Speeding overrides to the error red, exactly as the marker does. */
function stateDotVar(m: LiveVehicle): string {
  const tone = markerTone(m);
  if (m.speeding && tone === 'moving') return 'var(--raad-error)';
  return `var(${TONE_COLOR_TOKEN[tone]})`;
}

/** A compact position-age, unit-letter style (consistent with the non-localized
 *  "km/h" this screen already renders). Provisional until a localized relative time
 *  util exists in admin. */
function formatAge(sec: number | null | undefined): string | null {
  if (sec == null || !Number.isFinite(sec) || sec < 0) return null;
  if (sec < 60) return `${Math.round(sec)}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h`;
  return `${Math.round(sec / 86400)}d`;
}

function matches(v: AdminLiveVehicle, q: string): boolean {
  if (q === '') return true;
  const needle = q.toLowerCase();
  return (
    v.plate.toLowerCase().includes(needle) ||
    v.label.toLowerCase().includes(needle) ||
    v.tenantName.toLowerCase().includes(needle)
  );
}

function VehicleRow({
  v,
  marker,
  selected,
  onSelect,
}: {
  v: AdminLiveVehicle;
  marker: LiveVehicle;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const age = formatAge(v.positionAgeSec);
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className={[
          'w-full rounded-lg border px-3 py-2 text-left transition-colors',
          selected
            ? 'border-raad-primary bg-raad-primary/10'
            : 'border-transparent hover:bg-raad-primary/5',
        ].join(' ')}
      >
        <span className="flex items-center gap-2">
          <span
            className="size-2.5 flex-none rounded-full"
            style={{ background: stateDotVar(marker) }}
          />
          <span className="text-[13px] font-semibold">{v.label}</span>
          {/* The single most useful badge: the customer cannot see this vehicle. */}
          {!v.live ? (
            <span className="rounded border border-raad-primary/50 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-raad-accent">
              {t('live.notLive')}
            </span>
          ) : null}
        </span>
        <span className="mt-0.5 block text-[11.5px] text-raad-muted">
          {v.tenantName}
          {v.tenantStatus !== 'active' ? ` · ${t(`live.tenantStatus.${v.tenantStatus}`)}` : ''}
          {' · '}
          {t(`live.motion.${v.motion}`)}
          {v.speedKmh !== null && v.motion === 'moving'
            ? ` · ${String(Math.round(v.speedKmh))} km/h`
            : ''}
          {age !== null ? ` · ${age}` : ''}
        </span>
      </button>
    </li>
  );
}

/** The selected vehicle's install record + go-live gate — the same components the
 *  wizard and `/vehicles` render. Unchanged from slice 18f. */
function SelectedPanel({ selected }: { selected: AdminLiveVehicle }) {
  return (
    <div className="grid gap-4">
      <p className="text-[12px] font-semibold">{selected.label}</p>
      <InstallationPanel vehicleId={selected.vehicleId} />
      <GoLivePanel
        vehicleId={selected.vehicleId}
        liveAt={selected.liveAt}
        hiddenAt={selected.hiddenAt}
      />
    </div>
  );
}

/** matchMedia-driven phone check, guarded for jsdom (no matchMedia unless stubbed) —
 *  the same guard `LayersControl` uses. Default desktop, so existing tests render the
 *  two-column layout and the new mobile test stubs matchMedia to the phone branch. */
function useIsMobile(): boolean {
  const query = '(max-width: 767px)';
  const read = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false;
  const [mobile, setMobile] = useState(read);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const on = () => setMobile(mql.matches);
    on();
    mql.addEventListener?.('change', on);
    return () => mql.removeEventListener?.('change', on);
  }, []);
  return mobile;
}

/** Full-screen map height on a phone: the viewport below wherever this box lands
 *  (right under the admin top bar). Recomputed on resize. */
function useFullHeight(ref: RefObject<HTMLElement | null>): number | undefined {
  const [h, setH] = useState<number | undefined>(undefined);
  useEffect(() => {
    const calc = () => {
      const el = ref.current;
      if (!el || typeof window === 'undefined') return;
      const top = el.getBoundingClientRect().top;
      setH(Math.max(260, window.innerHeight - top));
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, [ref]);
  return h;
}

export function GlobalLive() {
  const { t } = useTranslation();
  const q = useAdminLive();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const isMobile = useIsMobile();
  const mobileWrapRef = useRef<HTMLDivElement>(null);
  const fullHeight = useFullHeight(mobileWrapRef);

  const vehicles = useMemo(
    () => (Array.isArray(q.data?.vehicles) ? q.data.vehicles : null),
    [q.data],
  );

  // Markers (shared shape) + the sort/filter derived from them. Hooks run before the
  // early returns below, so they are unconditional.
  const markerById = useMemo(() => {
    const m = new Map<string, LiveVehicle>();
    for (const v of vehicles ?? []) m.set(v.vehicleId, adminLiveToMarker(v));
    return m;
  }, [vehicles]);

  const allMarkers = useMemo(() => [...markerById.values()], [markerById]);
  const ringedIds = useMemo(
    () => new Set((vehicles ?? []).filter((v) => !v.live).map((v) => v.vehicleId)),
    [vehicles],
  );

  const rowsSorted = useMemo(() => {
    const byId = new Map((vehicles ?? []).map((v) => [v.vehicleId, v]));
    const filteredMarkers = (vehicles ?? [])
      .filter((v) => matches(v, query))
      .map((v) => markerById.get(v.vehicleId)!)
      .filter(Boolean);
    return sortByState(filteredMarkers)
      .map((m) => byId.get(m.id))
      .filter((v): v is AdminLiveVehicle => Boolean(v));
  }, [vehicles, query, markerById]);

  function select(id: string) {
    setSelectedId(id);
    // Phone: collapse the sheet to a peek so the map + the selected card are visible.
    setSheetExpanded(false);
  }

  if (q.isPending) return <p className="text-sm text-raad-muted">{t('live.loading')}</p>;
  if (q.isError || vehicles === null) {
    return (
      <p role="alert" className="text-sm text-raad-error">
        {t('live.error')}
      </p>
    );
  }

  const selected = vehicles.find((v) => v.vehicleId === selectedId) ?? null;
  // The phone sheet's resting height — shared with the map so its controls sit above it.
  const sheetCollapsedPx = selected ? 240 : 168;
  const notLive = vehicles.filter((v) => !v.live).length;
  const noFix = vehicles.filter((v) => v.lat === null).length;

  const heading = (
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
      <h1 id="live-heading" className="font-display text-xl font-bold tracking-tight">
        {t('live.title')}
      </h1>
      <p className="text-sm text-raad-muted">
        {t('live.summary', { count: vehicles.length })}
        {notLive > 0 ? ` · ${t('live.notLiveCount', { count: notLive })}` : ''}
      </p>
    </div>
  );

  const degradedAlert =
    q.data?.degraded === true ? (
      <p
        role="alert"
        className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[12.5px]"
      >
        {t('live.degraded')}
      </p>
    ) : null;

  const search = (
    <label className="block">
      <span className="sr-only">{t('live.searchLabel')}</span>
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('live.search')}
        aria-label={t('live.searchLabel')}
        className="w-full rounded-lg border border-raad-border bg-raad-input px-3 py-2 text-[13px] text-raad-text placeholder:text-raad-muted focus:border-raad-primary focus:outline-none"
      />
    </label>
  );

  const list =
    vehicles.length === 0 ? (
      <p className="text-sm text-raad-muted">{t('live.empty')}</p>
    ) : rowsSorted.length === 0 ? (
      <p className="text-[12.5px] text-raad-muted">{t('live.noMatch', { query })}</p>
    ) : (
      <ul className="space-y-1">
        {rowsSorted.map((v) => (
          <VehicleRow
            key={v.vehicleId}
            v={v}
            marker={markerById.get(v.vehicleId)!}
            selected={v.vehicleId === selectedId}
            onSelect={() => select(v.vehicleId)}
          />
        ))}
      </ul>
    );

  const noFixNote =
    noFix > 0 ? (
      <p className="mt-2 border-t border-raad-border pt-2 text-[11.5px] text-raad-muted">
        {t('live.noFix', { count: noFix })}
      </p>
    ) : null;

  const mapNode = (
    <Suspense
      fallback={
        <div className="flex size-full items-center justify-center bg-raad-panel">
          <span className="text-sm text-raad-muted" role="status">
            {t('live.mapLoading')}
          </span>
        </div>
      }
    >
      <GlobalFleetMap
        vehicles={allMarkers}
        ringedIds={ringedIds}
        selectedId={selectedId}
        onSelect={select}
        controlsBottomPx={isMobile ? sheetCollapsedPx + 12 : 12}
      />
    </Suspense>
  );

  // ── Phone: a full-screen map with the list in a bottom sheet ────────────────
  // Slice 84b — the `-mx-5 -my-6 sm:-mx-6` hack that used to escape the shell's
  // `main` padding is GONE. `AdminShell` now gives `/live` a padding-free `main`
  // below `lg` (route-aware full-bleed), so this section is already edge-to-edge.
  if (isMobile) {
    return (
      <section aria-labelledby="live-heading" data-testid="global-live-mobile">
        <h1 id="live-heading" className="sr-only">
          {t('live.title')}
        </h1>
        <div ref={mobileWrapRef} className="relative" style={{ height: fullHeight ?? '80dvh' }}>
          <div className="absolute inset-0">{mapNode}</div>
          <BottomSheet
            expanded={sheetExpanded}
            onExpandedChange={setSheetExpanded}
            label={t('live.listLabel')}
            expandLabel={t('live.sheet.expand')}
            collapseLabel={t('live.sheet.collapse')}
            collapsedPx={sheetCollapsedPx}
          >
            <div className="flex min-h-0 flex-1 flex-col gap-2 px-3 pb-3">
              <p className="text-[12px] text-raad-muted">
                {t('live.summary', { count: vehicles.length })}
                {notLive > 0 ? ` · ${t('live.notLiveCount', { count: notLive })}` : ''}
              </p>
              {degradedAlert}
              {search}
              {selected !== null ? (
                <div className="border-t border-raad-border pt-2">
                  <SelectedPanel selected={selected} />
                </div>
              ) : null}
              <div className="min-h-0 flex-1 overflow-y-auto">
                {list}
                {noFixNote}
              </div>
            </div>
          </BottomSheet>
        </div>
      </section>
    );
  }

  // ── Desktop: the two-column map + list (unchanged layout) ───────────────────
  return (
    <section aria-labelledby="live-heading">
      {heading}
      {degradedAlert ? <div className="mt-3">{degradedAlert}</div> : null}

      <div className="mt-4 grid gap-4 md:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="h-[28rem] overflow-hidden rounded-panel border border-raad-border">
          {mapNode}
        </div>

        <div className="rounded-panel border border-raad-border bg-raad-panel p-3">
          <div className="mb-2">{search}</div>
          <div className="max-h-[22rem] overflow-y-auto">{list}</div>
          {noFixNote}
          {selected !== null ? (
            <div className="mt-3 border-t border-raad-border pt-3">
              <SelectedPanel selected={selected} />
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export default GlobalLive;
